import { useState } from 'react';
import { Link } from 'react-router-dom';

import projectIconGeneric from '@Assets/images/project-icon-generic.svg';
import projectIconImg from '@Assets/images/project-icon-img.svg';
import projectIconPdf from '@Assets/images/project-icon-pdf.svg';
import projectIconXml from '@Assets/images/project-icon-xml.svg';

import { useProjectActions } from '../../hooks/project';
import { useDialog } from '../Dialog';
import { ProjectMenu } from './ProjectMenu';
import { ProjectRenameInput } from './ProjectRenameInput';
import type { ProjectListItemProps } from '../../types/project';
import {
  captureFunnelEvent,
  trackButtonClick,
  trackError,
} from '../../services/telemetry';

const PROJECT_TYPE_ICONS = {
  img: projectIconImg,
  pdf: projectIconPdf,
  xml: projectIconXml,
} as const;

// Item of project list in workspace page. each list item contains the project name, creation date, and a dropdown menu for project actions
export const ProjectListItem = ({
  project,
  onDelete,
  onRename,
}: ProjectListItemProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(project.name);
  const { renameProject, loading } = useProjectActions(true);
  const { showConfirm, showAlert, DialogComponent } = useDialog();

  const projectPath =
    project.fileType === 'xml'
      ? `/project/${project._id}`
      : `/pdf-project/${project._id}`;
  const projectIcon =
    (project.fileType && PROJECT_TYPE_ICONS[project.fileType]) ||
    projectIconGeneric;

  const handleRename = async () => {
    try {
      const updatedProject = await renameProject(project._id, newName);
      onRename({ _id: project._id, ...updatedProject });
      setIsRenaming(false);
    } catch {
      setNewName(project.name);
    }
    setIsMenuOpen(false);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const confirmed = await showConfirm(
      'Delete Project',
      <>
        <p>Are you sure you want to delete this project?</p>
        <p
          translate="no"
          className={`
            mt-2 overflow-hidden font-medium break-words text-gray-800
          `}
        >
          &quot;{project.name}&quot;
        </p>
        <p className="mt-2 text-sm text-gray-500">
          This action cannot be undone.
        </p>
      </>,
      { confirmText: 'Delete', cancelText: 'Cancel', confirmVariant: 'danger' }
    );

    if (!confirmed) return;
    setIsMenuOpen(false);
    try {
      await onDelete(project._id);
    } catch (error) {
      trackError(error as Error, { source: 'ProjectListItem.delete' });
      await showAlert(
        'Couldn’t delete project',
        'Something went wrong while deleting this project. Please try again.'
      );
    }
  };

  const formatLocalDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const cardClassName = `
    group relative flex min-w-0 items-center rounded-[8px] bg-white py-3.5
    pr-3 pl-3.5
    hover:bg-gray-50
  `;

  return (
    <>
      <DialogComponent />
      <div className={`${cardClassName} transition-colors`}>
        {/* Stretched link: covers the card so the whole row navigates, while
            the menu button (raised via z-index) stays independently clickable.
            Avoids nesting buttons inside an anchor (invalid HTML). */}
        {!isRenaming && (
          <Link
            to={projectPath}
            onClick={async () => {
              captureFunnelEvent('workspace_project_opened', {
                project_id: project._id,
                file_type: project.fileType ?? null,
              });
              await trackButtonClick('Workspace', 'Open Project', {
                projectId: project._id,
                fileType: project.fileType ?? null,
              });
            }}
            aria-label={`Open ${project.name}`}
            className="absolute inset-0 z-0 cursor-pointer rounded-[8px]"
          />
        )}

        {/* Project Name — flex-1 + min-w-0 so long names truncate instead of
            pushing the 3-dot menu off-screen. */}
        <div className="flex min-w-0 flex-1 items-center gap-3 pr-3">
          <img
            src={projectIcon}
            alt=""
            width={28}
            height={38}
            draggable={false}
            className="shrink-0"
          />
          {isRenaming ? (
            <div className="min-w-0 flex-1">
              <ProjectRenameInput
                value={newName}
                onChange={setNewName}
                onSubmit={handleRename}
                onCancel={() => {
                  setNewName(project.name);
                  setIsRenaming(false);
                }}
                isLoading={loading}
              />
            </div>
          ) : (
            <span
              className={`
                block min-w-0 truncate text-sm font-medium text-[#0d0d0d]
              `}
              title={project.name}
              translate="no"
            >
              {project.name}
            </span>
          )}
        </div>

        {/* Dates: Last modified, then Created (matches Workspace header).
            Never truncate — date/time must stay fully readable. */}
        <div
          className={`
            shrink-0 pr-2 whitespace-nowrap
            sm:w-1/4 sm:min-w-[150px] sm:pr-4
          `}
        >
          <p className="text-[13px] text-[#5d5d5d]">
            {formatLocalDateTime(project.updatedAt)}
          </p>
        </div>
        <div
          className={`
            hidden w-1/4 min-w-[150px] shrink-0 pr-4 whitespace-nowrap
            sm:block
          `}
        >
          <p className="text-[13px] text-[#5d5d5d]">
            {formatLocalDateTime(project.createdAt)}
          </p>
        </div>

        {/* Dropdown Menu — always reserved; never shrinks away */}
        <div className="relative z-[1] ml-auto shrink-0">
          <ProjectMenu
            isOpen={isMenuOpen}
            onToggle={() => setIsMenuOpen(!isMenuOpen)}
            onRename={() => {
              setIsRenaming(true);
              setIsMenuOpen(false);
            }}
            onDelete={handleDelete}
            isLoading={loading}
          />
        </div>
      </div>
    </>
  );
};
